import { convertRowsToLocalDateTimes } from "services/utils/sortDates"
import { EventsListType, EventsByResourceType, EventType, AvailabilityByDayType, IndividualJobEmployeeEvent } from "../../SchedulingTypes"
import { calculateDurationMinutesFromDateTimes, determineNearestTimeSlot, generateDayKey, generateTimeKeyFromDateTimeString } from "../../functions/schedulingDateUtils"

// Generate the project scheduler events from the fetched data
export const generateProjectEvents = (
    data: {
        employees: any[];
        availability: any[];
        project_job_appointments: any[];
        individual_job_appointments: any[];
    },
    userLocale: string,
    userTimezone: string,
    days: Date[]
): {
    generatedEvents: EventsListType;
    generatedEventsByResource: EventsByResourceType;
    generatedAvailabilityByDay: AvailabilityByDayType;
} => {
    // Initialize event variables
    const events: EventsListType = {}
    const eventsByResource: EventsByResourceType = {}
    const availabilityByDay: AvailabilityByDayType = {}

    // Generate a set with days
    const daySet = new Set(days.map(day => generateDayKey(day)))

    // Generate a mapping of employees
    const employeeNameMap: Record<number, string> = {}
    for (const employee of data.employees) {
        employeeNameMap[employee.id] = employee.full_name
    }
    

    /*
    Generate availability events
    */

    // Loop through the employees in the availability list
    for (const row of data.availability) {

        // Generate a day key
        const dayKey = generateDayKey(new Date(row.date))

        // Skip if the day set does not have the day key
        if (!daySet.has(dayKey)) continue

        // Loop through the blocks 
        for (const block of row.blocks) {

            // Generate event key
            const key = `availableEmployee-${row.user_id}-${dayKey}-${block.start}`

            // Generate start and end datetimes
            const startDateTime = `${row.date}T${block.start}:00`
            const endDateTime = `${row.date}T${block.end}:00`

            // Create an event per available block
            const event: EventType = {
                key,
                type: "availableEmployee",
                resourceId: "available_employees",
                startDateTime,
                endDateTime,
                dayKey,
                startTime: generateTimeKeyFromDateTimeString(startDateTime),
                endTime: generateTimeKeyFromDateTimeString(endDateTime),
                durationMinutes: calculateDurationMinutesFromDateTimes(startDateTime, endDateTime),
                timeSlot: determineNearestTimeSlot(startDateTime),
                employeeId: row.user_id,
                employeeName: row.full_name,
                deleted: false
            }

            // Add the event to the events state
            events[key] = event

            // Add the event to the availability by day state
            if (!availabilityByDay[dayKey]) {
                availabilityByDay[dayKey] = {}
            }

            if (!availabilityByDay[dayKey][row.user_id]) {
                availabilityByDay[dayKey][row.user_id] = []
            }

            availabilityByDay[dayKey][row.user_id].push(event)

            // Add the event key to the events by resource
            if (!eventsByResource["available_employees"]) {
                eventsByResource["available_employees"] = []
            }

            eventsByResource["available_employees"].push(key)
        }
    }

    /*
    Sort the employees in the availability by day on their full name
    */

    // Loop through the daykeys of the availability by day
    for (const dayKey of Object.keys(availabilityByDay)) {

        // Sort the employees on alphabetical order
        const employees = Object.keys(availabilityByDay[dayKey])
            .map(Number)
            .sort((a, b) => {
                const nameA = employeeNameMap[a] || ""
                const nameB = employeeNameMap[b] || ""

                return nameA.localeCompare(nameB)
            })

        const sorted: Record<number, EventType[]> = {}

        for (const employeeId of employees) {
            sorted[employeeId] = availabilityByDay[dayKey][employeeId]
        }

        availabilityByDay[dayKey] = sorted
    }


    /*
    Generate project job appointment events
    */

    // Convert the appointment datetime from utc to the local timezone
    const { rowsWithLocalDatetimes: localProjectJobAppointments } = convertRowsToLocalDateTimes(
        data.project_job_appointments, "start_datetime", "end_datetime", userLocale, userTimezone
    )

    // Loop through the project job appointments to generate events
    for (const item of localProjectJobAppointments) {

        // Generate a day key
        const dayKey = generateDayKey(new Date(item.start_datetime))

        // Skip if the day set does not have the day key
        if (!daySet.has(dayKey)) continue

        // Get the resource id
        const resourceId = String(item.job_id)
    
        // Loop through the assignee ids to create a separated event per assignee
        for (const assigneeId of (item.assignee_ids ?? [])) {

            // Get the name of the assignee
            const assigneeName = employeeNameMap[assigneeId]

            // Generate event key
            const key = `projectJobEmployeeEvent-${item.id}-${resourceId}-${assigneeId}`

            // Create the event per assignee
            const event: EventType = {
                key,
                type: "projectJobEmployeeEvent",
                appointmentId: item.id,
                resourceId,
                startDateTime: item.start_datetime,
                endDateTime: item.end_datetime,
                dayKey,
                startTime: generateTimeKeyFromDateTimeString(item.start_datetime),
                endTime: generateTimeKeyFromDateTimeString(item.end_datetime),
                durationMinutes: calculateDurationMinutesFromDateTimes(item.start_datetime, item.end_datetime),
                assigneeId,
                assigneeName,
                status: item.status,
                deleted: item.deleted ?? false
            }

            // Add the event to the events state
            events[key] = event

            // Add the resource id to the events by resource
            let resourceEvents = eventsByResource[resourceId]
            if (!resourceEvents) {
                resourceEvents = eventsByResource[resourceId] = []
            }

            // Add the event key to the events by resource
            resourceEvents.push(key)
        }
    }


    /*
    Generate individual job appointment events
    */

    // Convert the appointment datetime from utc to the local timezone
    const { rowsWithLocalDatetimes: localIndividualJobAppointments } = convertRowsToLocalDateTimes(
        data.individual_job_appointments, "start_datetime", "end_datetime", userLocale, userTimezone
    )

    // Loop through the individual job appointments to generate events
    for (const item of localIndividualJobAppointments) {

        // Generate a day key
        const dayKey = generateDayKey(new Date(item.start_datetime))

        // Skip if the day set does not have the day key
        if (!daySet.has(dayKey)) continue

        // Get the resource id
        const resourceId = String(item.job_id)
    
        // Loop through the assignee ids to create a separated event per assignee
        for (const assigneeId of (item.assignee_ids ?? [])) {

            // Get the name of the assignee
            const assigneeName = employeeNameMap[assigneeId]

            // Generate event key
            const key = `individualJobEmployeeEvent-${item.id}-${resourceId}-${assigneeId}`

            // Create the event per assignee
            const event: EventType = {
                key,
                type: "individualJobEmployeeEvent",
                appointmentId: item.id,
                resourceId,
                startDateTime: item.start_datetime,
                endDateTime: item.end_datetime,
                dayKey,
                startTime: generateTimeKeyFromDateTimeString(item.start_datetime),
                endTime: generateTimeKeyFromDateTimeString(item.end_datetime),
                durationMinutes: calculateDurationMinutesFromDateTimes(item.start_datetime, item.end_datetime),
                timeSlot: determineNearestTimeSlot(item.start_datetime),
                assigneeId,
                assigneeName,
                status: item.status,
                deleted: item.deleted ?? false
            }

            // Add the event to the events state
            events[key] = event

            // Add the resource id to the events by resource
            let resourceEvents = eventsByResource[resourceId]
            if (!resourceEvents) {
                resourceEvents = eventsByResource[resourceId] = []
            }

            // Add the event key to the events by resource
            resourceEvents.push(key)
        }
    }


    /*
    Stack overlapping individual job events
    */

    const slotGroups: Record<string, string[]> = {}

    // Group the events per resource, day and timeslot
    for (const key of Object.keys(events)) {
        const event = events[key]

        if (event.type === "individualJobEmployeeEvent") {
            // Generate the group key
            const groupKey = `${event.resourceId}-${event.dayKey}-${event.timeSlot}`

            // Add the group key
            if (!slotGroups[groupKey]) {
                slotGroups[groupKey] = []
            }

            // Add the event to the group key
            slotGroups[groupKey].push(key)
        }
    }

    // Sort the events and set the stack index
    for (const groupKey of Object.keys(slotGroups)) {

        const groupEvents = slotGroups[groupKey]
            .map(key => events[key] as IndividualJobEmployeeEvent)
            .sort((a, b) => (a.startDateTime ?? "").localeCompare(b.startDateTime ?? ""))

        groupEvents.forEach((event, index) => {
            event.stackIndex = index
        })
    }

    return {
        generatedEvents: events,
        generatedEventsByResource: eventsByResource,
        generatedAvailabilityByDay: availabilityByDay
    }
}